import { z } from "zod";
import { listQuerySchema } from "./pagination";
import { managerColorSchema } from "./colors";
import { localeSchema } from "./locale";
import { roleSchema, userStateSchema } from "./roles";

/**
 * A user as an administrator sees them. `state` and `credentialSet` are derived; no
 * password material appears here, by construction.
 */
export const adminUserSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  slug: z.string(),
  /** Palette key, or null when nobody has assigned one. */
  color: managerColorSchema.nullable(),
  email: z.string(),
  role: roleSchema,
  /**
   * The language they read the interface in. Reported, never administered: the switcher is
   * in the user's own profile menu, and `PATCH /users/me/locale` is the only writer.
   */
  locale: localeSchema,
  state: userStateSchema,
  credentialSet: z.boolean(),
  hasEntraIdentity: z.boolean(),
  active: z.boolean(),
  invitedAt: z.string().nullable(),
  invitedByName: z.string().nullable(),
  activatedAt: z.string().nullable(),
  lastSignInAt: z.string().nullable(),
  /** Open, non-deleted opportunities they own — what deactivating them would strand. */
  openOpportunities: z.number().int(),
  version: z.number().int(),
});

export type AdminUser = z.infer<typeof adminUserSchema>;

/**
 * A colleague as **anyone** may see them.
 *
 * Deliberately a different shape from `AdminUser`, not a subset picked at the call site:
 * everybody can open a team member's profile, so what a manager is allowed to learn about a
 * colleague has to be decided once, in the contract, rather than by remembering to omit
 * fields in a query.
 *
 * No email — the same judgement that removed it from the dev-user list. No invitation,
 * credential or sign-in fields: those are administration, and administration stays behind
 * `/api/users`, which remains Head-of-Sales-only.
 */
export const teamMemberProfileSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  name: z.string(),
  role: roleSchema,
  color: managerColorSchema.nullable(),
  /** Said plainly on the profile: a former colleague still owns their history. */
  state: userStateSchema,
  active: z.boolean(),
  /** Their live pipeline, derived server-side over open, non-deleted opportunities. */
  openOpportunities: z.number().int(),
  openPipelineValue: z.string(),
});

export type TeamMemberProfile = z.infer<typeof teamMemberProfileSchema>;

export const createUserSchema = z.object({
  name: z.string().trim().min(1, "Enter a name").max(120),
  email: z.string().trim().toLowerCase().email("Enter a valid email address"),
  role: roleSchema,
  /** Optional by design: an administrator may hand the link over instead of emailing it. */
  sendInvitation: z.boolean().default(false),
  /**
   * The board colour, chosen at creation (2026-08-26).
   *
   * It was assignable only afterwards, from the row — which meant every new manager appeared
   * on the board uncoloured until somebody remembered. Optional, because "decide later" is a
   * legitimate answer and `null` is what the column already means.
   */
  color: managerColorSchema.nullable().optional(),
});
export type CreateUserInput = z.infer<typeof createUserSchema>;

export const updateUserRoleSchema = z.object({ role: roleSchema, version: z.number().int() });
export const setUserActiveSchema = z.object({ active: z.boolean(), version: z.number().int() });

export const usersQuerySchema = listQuerySchema.extend({
  role: roleSchema.optional(),
  state: userStateSchema.optional(),
});

export const USER_SORT_FIELDS = ["name", "email", "role", "lastSignInAt"] as const;

/**
 * The response to issuing an invitation. `link` is returned so the screen can offer it for
 * copying — the book requires that path for when an address is wrong or the person is
 * standing next to you, and `emailSent` says plainly whether delivery was attempted.
 */
export const invitationResultSchema = z.object({
  link: z.string(),
  expiresAt: z.string(),
  emailSent: z.boolean(),
});
export type InvitationResult = z.infer<typeof invitationResultSchema>;
